import React, { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Calendar,
  Folder,
  Users,
  Ticket,
  Settings,
  Plus,
  UserCog,
  Tags,
  Menu,
  X,
  NotepadText,
  QrCode,
  MapPin,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { setAdminMobileNavOpen, useAdminMobileNav } from "./adminMobileNav";

const AdminSidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const mobileOpen = useAdminMobileNav();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const closeMobileNav = () => setAdminMobileNavOpen(false);

  useEffect(() => {
    if (!mobileOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMobileNav();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname]);

  const NavItem = ({ to, icon, label, forceExpanded = false }) => {
    const collapsed = !forceExpanded && isCollapsed;
    return (
      <Link
        to={to}
        onClick={closeMobileNav}
        className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
          isActive(to)
            ? "bg-white/10 text-white"
            : "text-gray-400 hover:text-white hover:bg-white/5"
        } ${collapsed ? "justify-center px-2" : ""}`}
        title={collapsed ? label : ""}
      >
        {icon}
        {!collapsed && <span>{label}</span>}
      </Link>
    );
  };

  const SidebarChrome = ({ forceExpanded = false, showClose = false }) => {
    const collapsed = !forceExpanded && isCollapsed;

    return (
      <>
        <div
          className={`p-4 border-b border-white/10 flex items-center ${
            collapsed ? "justify-center" : "justify-between"
          }`}
        >
          {!collapsed && (
            <img
              src="/assets/echojham.logo2.png"
              alt="Echo Jham"
              className="h-14"
              onError={(e) => {
                e.target.src = "https://via.placeholder.com/100?text=EJ";
              }}
            />
          )}

          {showClose ? (
            <button
              type="button"
              onClick={closeMobileNav}
              className="p-2 min-h-10 min-w-10 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white"
              aria-label="Close navigation"
            >
              <X size={20} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 min-h-10 min-w-10 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <Menu size={20} />
            </button>
          )}
        </div>

        <div className="p-4">
          <button
            type="button"
            onClick={() => {
              closeMobileNav();
              navigate("/admin/create-event");
            }}
            className={`flex items-center justify-center gap-2 bg-brand-maroon hover:bg-brand-red py-2.5 rounded-lg text-sm font-bold text-white transition-all shadow-lg w-full ${
              collapsed ? "px-0" : "px-4"
            }`}
          >
            <Plus size={18} />
            {!collapsed && <span>Add New Event</span>}
          </button>
        </div>

        <nav className="flex-1 px-3 space-y-1 overflow-y-auto pt-2 custom-scrollbar">
          {!collapsed && (
            <p className="text-[10px] text-gray-600 font-bold mb-2 ml-2 tracking-widest uppercase">
              Dashboard
            </p>
          )}
          <NavItem
            to="/admin/dashboard"
            icon={<LayoutDashboard size={20} />}
            label="Main Overview"
            forceExpanded={forceExpanded}
          />
          {collapsed && <div className="mx-3 my-2 border-t border-white/10" />}

          {!collapsed && (
            <p className="text-[10px] text-gray-600 font-bold mb-2 mt-6 ml-2 tracking-widest uppercase">
              Client Services
            </p>
          )}
          <NavItem to="/admin/bookings" icon={<Calendar size={20} />} label="Bookings" forceExpanded={forceExpanded} />
          <NavItem to="/admin/projects" icon={<Folder size={20} />} label="Projects" forceExpanded={forceExpanded} />
          <NavItem to="/admin/clients" icon={<Users size={20} />} label="Clients" forceExpanded={forceExpanded} />
          {collapsed && <div className="mx-3 my-2 border-t border-white/10" />}

          {!collapsed && (
            <p className="text-[10px] text-gray-600 font-bold mb-2 mt-6 ml-2 tracking-widest uppercase">
              Box Office
            </p>
          )}
          <NavItem to="/admin/featured" icon={<Users size={20} />} label="Featured Events" forceExpanded={forceExpanded} />
          <NavItem to="/admin/venues" icon={<MapPin size={20} />} label="Venues" forceExpanded={forceExpanded} />
          <NavItem to="/admin/liveevents" icon={<Ticket size={20} />} label="Live Events" forceExpanded={forceExpanded} />
          <NavItem to="/admin/tickets" icon={<QrCode size={20} />} label="Tickets" forceExpanded={forceExpanded} />
          <NavItem to="/admin/orders" icon={<Tags size={20} />} label="Orders" forceExpanded={forceExpanded} />
          <NavItem to="/admin/customers" icon={<Users size={20} />} label="Customers" forceExpanded={forceExpanded} />
          {collapsed && <div className="mx-3 my-2 border-t border-white/10" />}

          {!collapsed && (
            <p className="text-[10px] text-gray-600 font-bold mb-2 mt-6 ml-2 tracking-widest uppercase">
              System
            </p>
          )}
          <NavItem to="/admin/support" icon={<NotepadText size={20} />} label="Support" forceExpanded={forceExpanded} />
          <NavItem to="/admin/settings" icon={<Settings size={20} />} label="Settings" forceExpanded={forceExpanded} />
          <NavItem to="/admin/accounts" icon={<UserCog size={20} />} label="Accounts" forceExpanded={forceExpanded} />
        </nav>
      </>
    );
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex bg-black text-white flex-col h-screen sticky top-0 transition-all duration-300 shadow-2xl z-50 overflow-hidden ${
          isCollapsed ? "w-20" : "w-64"
        }`}
      >
        <SidebarChrome />
      </aside>

      {/* Mobile / tablet drawer */}
      {mobileOpen ? (
        <button
          type="button"
          aria-label="Close navigation overlay"
          className="fixed inset-0 z-[60] bg-black/40 lg:hidden"
          onClick={closeMobileNav}
        />
      ) : null}

      <aside
        className={`fixed inset-y-0 left-0 z-[70] flex w-64 max-w-[85vw] flex-col bg-black text-white shadow-2xl transition-transform duration-300 lg:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full pointer-events-none"
        }`}
        aria-hidden={!mobileOpen}
      >
        <SidebarChrome forceExpanded showClose />
      </aside>
    </>
  );
};

export default AdminSidebar;
